import dayjs from "dayjs";

/**
 * Generates an option symbol in the format expected by FDS
 * @param underlying - The underlying stock symbol (e.g., "TSLA")
 * @param expirationDate - The expiration date string
 * @param action - The action (BUY/SELL)
 * @param strikePrice - The strike price number
 * @returns Formatted option symbol (e.g., "TSLA#250718C00325000-USA")
 */
export const generateOptionSymbol = (
  underlying: string,
  expirationDate: string,
  action: string,
  strikePrice: number,
): string => {
  const paddedUnderlying = underlying.padEnd(6, " ");
  const expDate = dayjs(expirationDate).format("YYMMDD");
  const strikePriceNum =
    typeof strikePrice === "number" ? strikePrice : parseFloat(strikePrice);
  const formattedStrike = Number(strikePriceNum.toFixed(2))
    .toFixed(3)
    .replace(".", "")
    .padStart(8, "0");
  const optionType = action === "BUY" ? "C" : "P";
  return `${paddedUnderlying}${expDate}${optionType}${formattedStrike}-USA`.replace(
    / +/g,
    "#",
  );
};

/**
 * Extracts stock data from FDS cache
 * @param symbol - The stock symbol
 * @param FDS_stockData - The FDS stock data cache
 * @returns Object with lastPrice and raw data
 */
export const getFDSStockData = (symbol: string, FDS_stockData: any) => {
  const fdsStock = FDS_stockData[symbol];
  return {
    lastPrice:
      fdsStock?.last_price && fdsStock.last_price !== "N/A"
        ? fdsStock.last_price
        : "N/A",
    data: fdsStock,
  };
};

/**
 * Extracts option data from FDS cache
 * @param optionSymbol - The option symbol
 * @param FDS_optionData - The FDS option data cache
 * @returns Object with lastPrice, delta, and raw data
 */
export const getFDSOptionData = (optionSymbol: string, FDS_optionData: any) => {
  const symbolKey = Object.keys(FDS_optionData).find((key) =>
    key.startsWith(optionSymbol.split("#")[0]),
  );
  const fdsOption = symbolKey ? FDS_optionData[symbolKey] : null;
  return {
    lastPrice:
      fdsOption?.last_price && fdsOption.last_price !== "N/A"
        ? fdsOption.last_price
        : "N/A",
    delta:
      fdsOption?.delta && fdsOption.delta !== "N/A" ? fdsOption.delta : "N/A",
    symbolKey: symbolKey,
    data: fdsOption,
  };
};

/**
 * Processes legs array and enriches with FDS data
 * @param legs - Array of option legs
 * @param symbol - The underlying stock symbol
 * @param FDS_optionData - The FDS option data cache
 * @returns Array of legs with FDS data
 */
export const processLegsWithFDSData = (
  legs: Array<{
    action: string;
    strike_price: number;
    expiration_date: string;
  }>,
  symbol: string,
  FDS_optionData: any,
) => {
  return (
    legs?.map(
      (leg: {
        action: string;
        strike_price: number;
        expiration_date: string;
      }) => {
        const optionSymbol = generateOptionSymbol(
          symbol,
          leg.expiration_date,
          leg.action,
          leg.strike_price,
        );
        const optionData = getFDSOptionData(optionSymbol, FDS_optionData);

        return {
          ...leg,
          optionSymbol,
          optionLastPrice: optionData.lastPrice,
          optionDelta: optionData.delta,
        };
      },
    ) || []
  );
};

/**
 * Extracts stock symbols from alerts for FDS subscription
 * @param alerts - Array of alert objects
 * @returns Array of stock symbols with "-USA" suffix
 */
export const extractStockSymbols = (alerts: any[]): string[] => {
  return alerts
    .filter((alert) => alert.type === "Trade Signal" && alert.symbol)
    .map((alert) => alert.symbol + "-USA");
};

/**
 * Extracts option symbols from alerts for FDS subscription
 * @param alerts - Array of alert objects
 * @returns Array of option symbols
 */
export const extractOptionSymbols = (alerts: any[]): string[] => {
  return alerts
    .filter(
      (alert) =>
        alert.type === "Trade Signal" &&
        alert.legs &&
        alert.legs.length > 0 &&
        alert.symbol,
    )
    .flatMap(
      (alert) =>
        alert.legs?.map(
          (leg: {
            action: string;
            strike_price: number;
            expiration_date: string;
          }) =>
            generateOptionSymbol(
              alert.symbol!,
              leg.expiration_date,
              leg.action,
              leg.strike_price,
            ),
        ) || [],
    );
};
